import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appEnv } from '../../../app.config';
import { CallingConfig, CallLogRecord, CallProgressStatus, CrmSource, LogCallPayload, MissedCall, UpdateCallPayload, WebRtcConfig } from '../models/call-log.model';

@Injectable({
  providedIn: 'root'
})
export class CallSendService {
  constructor(private httpClient: HttpClient) {}

  /** Places a real outbound call via Ringotel Voice - see
   *  RingotelVoiceCallController::initiate(). 403s with a clear message if
   *  calling isn't configured yet (checked proactively via configured()
   *  below, not just on failure). */
  initiate(crmSource: CrmSource, subjectUuid: string, to: string): Observable<{ call_log_id: number; status: string }> {
    return this.httpClient.post<{ call_log_id: number; status: string }>(`${appEnv.api}crm/messaging/calls/initiate`, {
      crm_source: crmSource,
      subject_uuid: subjectUuid,
      to
    });
  }

  /** This record's call history - see RingotelVoiceCallController::index().
   *  Passing phoneNumber also merges in Commstack's call log for that
   *  number; omit/null skips that fetch entirely. */
  list(crmSource: CrmSource, subjectUuid: string, phoneNumber?: string | null): Observable<{ data: CallLogRecord[] }> {
    const params: Record<string, string> = { crm_source: crmSource, subject_uuid: subjectUuid };
    if (phoneNumber) params['phone_number'] = phoneNumber;

    return this.httpClient.get<{ data: CallLogRecord[] }>(`${appEnv.api}crm/messaging/calls`, { params });
  }

  /** Logs a call by hand (outcome, notes, duration) - no Ringotel involved,
   *  so it works even when calling isn't configured. See
   *  RingotelVoiceCallController::store(). */
  log(payload: LogCallPayload): Observable<{ data: CallLogRecord }> {
    return this.httpClient.post<{ data: CallLogRecord }>(`${appEnv.api}crm/messaging/calls`, payload);
  }

  /** Records an outbound call about to be dialled from the admin panel -
   *  'webrtc' (in-browser Call console, as our registered number) or
   *  'softphone' (the agent's Ringotel softphone via a tel: link). See
   *  RingotelVoiceCallController::dial(). */
  dial(crmSource: CrmSource, subjectUuid: string, to: string, via: 'webrtc' | 'softphone'): Observable<{ data: CallLogRecord }> {
    return this.httpClient.post<{ data: CallLogRecord }>(`${appEnv.api}crm/messaging/calls/dial`, {
      crm_source: crmSource,
      subject_uuid: subjectUuid,
      to,
      via
    });
  }

  /** Live status of an in-browser call - see RingotelVoiceCallController::progress(). */
  progress(id: number, status: CallProgressStatus, durationSeconds: number | null = null): Observable<{ data: CallLogRecord }> {
    return this.httpClient.put<{ data: CallLogRecord }>(`${appEnv.api}crm/messaging/calls/${id}/progress`, {
      status,
      duration_seconds: durationSeconds
    });
  }

  /** Recent missed calls across every CRM + how many are unseen - see
   *  RingotelVoiceCallController::missed(). */
  missed(): Observable<{ data: MissedCall[]; unseen: number }> {
    return this.httpClient.get<{ data: MissedCall[]; unseen: number }>(`${appEnv.api}crm/messaging/calls/missed`);
  }

  /** Marks missed calls seen by the current admin (all unseen when ids is empty). */
  markMissedSeen(ids: number[] = []): Observable<{ updated: number }> {
    return this.httpClient.post<{ updated: number }>(`${appEnv.api}crm/messaging/calls/missed/seen`, { ids });
  }

  /** SIP line details for the Call console - see
   *  RingotelVoiceCallController::webrtcConfig(). */
  webrtcConfig(): Observable<{ data: WebRtcConfig }> {
    return this.httpClient.get<{ data: WebRtcConfig }>(`${appEnv.api}crm/messaging/calls/webrtc-config`);
  }

  /** Sets outcome/duration/notes on a manual or softphone call - see
   *  RingotelVoiceCallController::update(). */
  update(id: number, payload: UpdateCallPayload): Observable<{ data: CallLogRecord }> {
    return this.httpClient.put<{ data: CallLogRecord }>(`${appEnv.api}crm/messaging/calls/${id}`, payload);
  }

  /** Whether real Ringotel Voice credentials exist yet - see
   *  RingotelVoiceCallController::configured(). */
  configured(): Observable<CallingConfig> {
    return this.httpClient.get<CallingConfig>(`${appEnv.api}crm/messaging/calls/configured`);
  }
}
